/* eslint-disable */
/*
 * Method figures built on the Hairline kernel (MIT, © 2026 Lucas Marques — see kernel.ts).
 * Geometry, camera and motion are copied verbatim from inbox.js, hairline-filing.html
 * (accepted version) and report.js. Only change: the hover test covers the whole stage
 * so moving geometry never re-triggers the animation.
 */
import HairlineFigure from '@/components/feature/hairline/HairlineFigure';
import {
  Cam, fit, proj, facing, rings, prism, solid, put, mk, seg, fillet, poly, open,
  tween, tset, tval, tdone, register, pointer, disposer,
} from '@/components/feature/hairline/kernel';

const whole = (choose) => ({ move: () => choose(true), leave: () => choose(false) });

/* FIG 01 — inbox.js */
function mountInbox({ stage, svg, read }, value) {
  const bag = disposer(); let stagger = value, active = false;
  const C = Cam(45, .5, 1.85);
  fit(C, [[0, -4, 0], [76, 98, 0], [0, -4, 73], [76, 98, 73]], 200, 166);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg);
  for (const x of [1, 71]) { const [r, n] = rings(x, 1, x + 3, 5, 1, .6); put(solid(g), prism(P, front, r, n, 0, 64)); }
  const trays = [], rests = [3, 6, 0], counts = [2, 7, 1];
  for (let i = 0; i < 3; i++) {
    const group = mk('g', {}, g), floor = solid(group), back = solid(group), left = solid(group), papers = [];
    for (let j = 0; j < counts[i]; j++) papers.push(solid(group));
    const right = solid(group), lip = mk('path', { class: 'sil' }, group), crease = mk('path', { class: 'nf lo' }, group);
    trays.push({ group, floor, back, left, right, papers, lip, crease, t: tween(rests[i]), last: NaN });
  }
  function box(el, x0, y0, x1, y1, z0, z1, r = 1.5) { const [a, b] = rings(x0, y0, x1, y1, r, .6); put(el, prism(P, front, a, b, z0, z1)); }
  function draw(i, shift) {
    const t = trays[i], z = 6 + i * 22, y = shift;
    box(t.floor, 0, y, 76, y + 66, z, z + 2, 3);
    box(t.back, 0, y, 76, y + 3, z + 2, z + 13, 1);
    box(t.left, 0, y + 3, 3, y + 66, z + 2, z + 11, 1);
    t.papers.forEach((sheet, j) => box(sheet, 7 + (j % 2) * .5, y + 8, 68 + (j % 2) * .5, y + 58, z + 3 + j * 1.05, z + 3.65 + j * 1.05, 1));
    box(t.right, 73, y + 3, 76, y + 66, z + 2, z + 11, 1);
    const edge = [[0, 0], [76, 0], [76, 6], [46, 6], [43, 3], [33, 3], [30, 6], [0, 6]];
    t.lip.setAttribute('d', poly(fillet(edge, edge.map(() => .8)).map(([x, h]) => P(x, y + 66, z + 2 + h))));
    t.crease.setAttribute('d', seg(P(5, y + 66, z + 3), P(71, y + 66, z + 3)));
    t.last = shift;
  }
  trays.forEach((t, i) => draw(i, rests[i])); trays[1].lip.classList.add('hi');
  const B = register(stage, (_dt, now) => { let moving = false; trays.forEach((t, i) => { const v = tval(t.t, now); if (v !== t.last) draw(i, v); if (!tdone(t.t, now)) moving = true; }); return moving; });
  bag.add(B.unregister);
  function choose(on) { if (on === active) return; active = on; const now = performance.now(); trays.forEach((t, i) => tset(t.t, on ? [7, 32, -3][i] : rests[i], now, Math.abs(i - 1) * stagger)); read.textContent = on ? 'backlog' : 'rest'; B.wake(); }
  bag.add(pointer(stage, whole(choose)));
  bag.add(() => svg.replaceChildren()); return { set: (v) => { stagger = v; }, destroy: bag.dispose };
}

/* FIG 02 — hairline-filing.html (accepted) */
function mountFiling({ stage, svg, read }, value) {
  const bag = disposer(); let stagger = value, active = false;
  const C = Cam(45, 0.5, 1.95);
  fit(C, [[-24, 28, 0], [76, 104, 0], [-24, 28, 84], [76, 104, 84]], 200, 166);
  const P = proj(C), g = mk('g', {}, svg);
  function folderPath(y, top, tab, lean) {
    const outline = tab ? [[0, 0], [70, 0], [70, top], [34, top], [30, top + 8], [8, top + 8], [4, top], [0, top]] : [[0, 0], [70, 0], [70, top], [0, top]];
    return poly(fillet(outline, outline.map(() => 2)).map(([x, z]) => P(x, y + lean * z, z)));
  }
  mk('path', { d: folderPath(28, 63, true, 0), class: 'sil' }, g);
  mk('path', { d: poly([P(0, 28, 0), P(70, 28, 0), P(70, 34, 0), P(0, 34, 0)]), class: 'lo' }, g);
  const frontGroup = mk('g', {}, g);
  const sides = [0, 70].map((x) => ({ x, face: mk('path', { class: 'sil' }, frontGroup), fold: mk('path', { class: 'nf lo' }, frontGroup) }));
  const front = mk('path', { class: 'sil hi' }, frontGroup);
  const seam = mk('path', { class: 'nf lo' }, frontGroup);
  const cards = [];
  const rests = [[-22, 76, 12, -2], [-18, 84, 14, 0], [-14, 92, 16, 2]];
  for (let i = 0; i < 3; i++) {
    const group = mk('g', {}, g), face = mk('path', { class: 'sil' }, group), fold = mk('path', { class: 'nf lo' }, group), lines = mk('path', { class: 'nf lo' }, group);
    cards.push({ group, face, fold, lines, t: tween(0), last: NaN, y: 0 });
  }
  function drawCard(i, t) {
    const c = cards[i], r = rests[i], x = r[0] + (5 + i * .7 - r[0]) * t, y = r[1] + (29 + i * 1.1 - r[1]) * t;
    const lift = r[2] * (1 - t) + 4 * t + 20 * Math.sin(Math.PI * t), angle = r[3] * (1 - t) * Math.PI / 180;
    const point = (u, v) => P(x + u * Math.cos(angle) - v * Math.sin(angle), y, lift + u * Math.sin(angle) + v * Math.cos(angle));
    const outline = [[0, 0], [58, 0], [58, 48], [48, 58], [0, 58]];
    c.face.setAttribute('d', poly(fillet(outline, outline.map(() => 1.2)).map(([u, v]) => point(u, v))));
    c.fold.setAttribute('d', open([point(48, 58), point(48, 48), point(58, 48)]));
    c.lines.setAttribute('d', [37, 29, 21].map((z, k) => seg(point(7, z), point(k === 2 ? 36 : 49, z))).join(''));
    c.y = y; c.last = t;
  }
  function drawFront(t) {
    const lean = .035 + .07 * Math.sin(Math.PI * t);
    sides.forEach(({ x, face, fold }) => {
      const edge = [[28, 0], [34, 0], [34 + lean * 56, 56], [28, 56]];
      face.setAttribute('d', poly(fillet(edge, edge.map(() => .7)).map(([y, z]) => P(x, y, z))));
      fold.setAttribute('d', open([P(x, 31, 1), P(x, 31 + lean * 28, 28), P(x, 31 + lean * 28, 54)]));
    });
    front.setAttribute('d', folderPath(34, 56, false, lean));
    seam.setAttribute('d', seg(P(6, 34 + lean * 51, 51), P(64, 34 + lean * 51, 51)));
  }
  cards.forEach((c, i) => drawCard(i, 0)); drawFront(0);
  function order() {
    const entries = cards.map((c) => ({ group: c.group, y: c.y })); entries.push({ group: frontGroup, y: 38 });
    entries.sort((a, b) => a.y - b.y).forEach((e) => g.appendChild(e.group));
  }
  order();
  let lastSum = NaN;
  const B = register(stage, (_dt, now) => {
    let moving = false, sum = 0;
    cards.forEach((c, i) => { const t = tval(c.t, now); sum += t; if (t !== c.last) drawCard(i, t); if (!tdone(c.t, now)) moving = true; });
    if (sum !== lastSum) { drawFront(sum / 3); order(); lastSum = sum; }
    front.classList.toggle('hi', !active || !moving); cards[1].face.classList.toggle('hi', active && moving);
    read.textContent = active ? (moving ? 'filing' : 'filed') : 'rest'; return moving;
  }); bag.add(B.unregister);
  function choose(on) {
    if (on === active) return; active = on;
    const now = performance.now(); cards.forEach((c, i) => tset(c.t, on ? 1 : 0, now, Math.abs(i - 1) * stagger));
    front.classList.toggle('hi', !on); cards[1].face.classList.toggle('hi', on);
    B.wake();
  }
  bag.add(pointer(stage, whole(choose)));
  bag.add(() => svg.replaceChildren());
  return { set: (v) => { stagger = v; }, destroy: bag.dispose };
}

/* FIG 03 — report.js */
function mountReport({ stage, svg, read }, value) {
  const bag = disposer(), C = Cam(45, .5, 2.15); let stagger = value, active = false;
  fit(C, [[0, 0, 0], [104, 62, 0], [0, 0, 69], [104, 62, 69]], 200, 150);
  const P = proj(C), front = facing(C), g = mk('g', {}, svg);
  function box(el, x, y, w, d, z, h, r = 1) { const [ring, inner] = rings(x, y, x + w, y + d, r, .6); put(el, prism(P, front, ring, inner, z, z + h)); }
  box(solid(g), 0, 0, 104, 62, 0, 3, 3);
  const entries = [], bars = [], rest = [18, 25, 22], target = [35, 49, 66];
  for (let i = 0; i < 3; i++) { const x = 9 + i * 31; entries.push({ x, y: 9, baseline: true, i }, { x, y: 34, baseline: false, i }); }
  entries.sort((a, b) => a.x + a.y - b.x - b.y).forEach((e) => {
    const el = solid(g);
    if (e.baseline) { el.sil.classList.add('lo'); el.cr.classList.add('lo'); box(el, e.x, e.y, 17, 17, 3, rest[e.i]); }
    else { el.sil.classList.add('hi'); bars[e.i] = { el, x: e.x, y: e.y, tw: tween(rest[e.i]), last: rest[e.i] }; box(el, e.x, e.y, 17, 17, 3, rest[e.i]); }
  });
  const ticks = mk('path', { class: 'nf lo' }, g);
  ticks.setAttribute('d', [35, 66].map((x) => seg(P(x, 58, 3), P(x, 62, 3))).join('')); read.textContent = 'baseline';
  const B = register(stage, (_dt, now) => { let moving = false; bars.forEach((b) => { const h = tval(b.tw, now); if (h !== b.last) { box(b.el, b.x, b.y, 17, 17, 3, h); b.last = h; } if (!tdone(b.tw, now)) moving = true; }); return moving; }); bag.add(B.unregister);
  function choose(on) { if (on === active) return; active = on; const now = performance.now(); bars.forEach((b, i) => tset(b.tw, on ? target[i] : rest[i], now, i * stagger)); read.textContent = on ? 'improved' : 'baseline'; B.wake(); }
  bag.add(pointer(stage, whole(choose)));
  bag.add(() => svg.replaceChildren()); return { set: (v) => { stagger = v; }, destroy: bag.dispose };
}

export function DiscoverFigure() {
  return <HairlineFigure name="inbox" means="Find where work slows down. Three document trays separate to reveal the overloaded middle tray." mount={mountInbox} stagger={45} />;
}

export function ImplementFigure() {
  return <HairlineFigure name="filing" means="Documents slide into a thin, closed-sided file sleeve." mount={mountFiling} stagger={45} />;
}

export function ImproveFigure() {
  return <HairlineFigure name="report" means="Paired columns compare the baseline with improved performance." mount={mountReport} stagger={65} />;
}

export const methodFigureStyles = `
.hl-stage{width:100%;--hairline-plate:oklch(var(--background-50))}
`;